import React from 'react';
import { AdminDashboardContainer } from '@containers-admin/AdminDashboard';
import { Grid, Spinner, Text, SVGIcon, GridContainer, GridRow, InfoContainer, StyledTable } from '@custom-components';
import PageSEO from '../../../components/PageSEO';
import LocaleLink from '../../../components/LocaleLink';
import { CardLink, DetailItem, cellStyle } from '../../../components/Dashboard/Dashboard.styles';
import { Secure } from '../../../containers/admin/Secure/Secure';
import { permission } from '../../../containers/admin/Secure/permission';
import Error from '../../_error';
import { ActionLink, GridCol, PortalSVGIcon, colors } from '../../../customComponents';

const renderDetailRows = (
  loading,
  { checkingInToday, checkingOutToday, createdToday, updatedToday },
  { numberOfOrgRequests, numberOfUserRequests, numberOfCreditApplications }
) => [
  {
    id: 'labels',
    checkin: (
      <Text.CopyBody noMargins tagName="span">
        Checking In
      </Text.CopyBody>
    ),
    checkout: (
      <Text.CopyBody noMargins tagName="span">
        Checking Out
      </Text.CopyBody>
    ),
    created: (
      <Text.CopyBody noMargins tagName="span">
        Created
      </Text.CopyBody>
    ),
    updated: (
      <Text.CopyBody noMargins tagName="span">
        Updated
      </Text.CopyBody>
    ),
    organizationRequests: (
      <Text.CopyBody noMargins tagName="span">
        Pending Org Requests
      </Text.CopyBody>
    ),
    userRequests: (
      <Text.CopyBody noMargins tagName="span">
        Pending User Requests
      </Text.CopyBody>
    ),
    creditApplications: (
      <Text.CopyBody noMargins tagName="span">
        Pending Credit Applications
      </Text.CopyBody>
    )
  },
  {
    id: 'stats',
    checkin: (
      <DetailItem>
        <SVGIcon className="icon" selectedIcon="booking-change" width="24px" height="24px" />
        {loading ? (
          <Spinner size={14} />
        ) : (
          <Text.CopyBody noMargins tagName="span">
            {checkingInToday}
          </Text.CopyBody>
        )}
      </DetailItem>
    ),
    checkout: (
      <DetailItem>
        <SVGIcon className="icon" selectedIcon="booking-change" width="24px" height="24px" />
        {loading ? (
          <Spinner size={14} />
        ) : (
          <Text.CopyBody noMargins tagName="span">
            {checkingOutToday}
          </Text.CopyBody>
        )}
      </DetailItem>
    ),
    created: (
      <DetailItem>
        <SVGIcon className="icon" selectedIcon="booking-change" width="24px" height="24px" />
        {loading ? (
          <Spinner size={14} />
        ) : (
          <Text.CopyBody noMargins tagName="span">
            {createdToday}
          </Text.CopyBody>
        )}
      </DetailItem>
    ),
    updated: (
      <DetailItem>
        <SVGIcon className="icon" selectedIcon="booking-change" width="24px" height="24px" />
        {loading ? (
          <Spinner size={14} />
        ) : (
          <Text.CopyBody noMargins tagName="span">
            {updatedToday}
          </Text.CopyBody>
        )}
      </DetailItem>
    ),
    organizationRequests: (
      <DetailItem>
        <SVGIcon className="icon" selectedIcon="booking-change" width="24px" height="24px" />
        {loading ? (
          <Spinner size={14} />
        ) : (
          <Text.CopyBody tagName="p" color="quaternary" noMargins>
            <LocaleLink route="OrgRequests" passHref>
              <ActionLink noPadding underline>
                <div>{numberOfOrgRequests}</div>
              </ActionLink>
            </LocaleLink>
          </Text.CopyBody>
        )}
      </DetailItem>
    ),
    userRequests: (
      <DetailItem>
        <SVGIcon className="icon" selectedIcon="booking-change" width="24px" height="24px" />
        {loading ? (
          <Spinner size={14} />
        ) : (
          <Text.CopyBody tagName="p" color="quaternary" noMargins>
            <LocaleLink route="UserRequests">
              <ActionLink noPadding underline>
                <div>{numberOfUserRequests}</div>
              </ActionLink>
            </LocaleLink>
          </Text.CopyBody>
        )}
      </DetailItem>
    ),
    creditApplications: (
      <DetailItem>
        <SVGIcon className="icon" selectedIcon="booking-change" width="24px" height="24px" />
        {loading ? (
          <Spinner size={14} />
        ) : (
          <Text.CopyBody tagName="p" color="quaternary" noMargins>
            <LocaleLink route="PendingCreditRequests">
              <ActionLink noPadding underline>
                <div>{numberOfCreditApplications}</div>
              </ActionLink>
            </LocaleLink>
          </Text.CopyBody>
        )}
      </DetailItem>
    )
  }
];

const iconSize = '48px';

/**
 * Home Page / Dashboard
 */
function Dashboard() {
  return (
    <>
      <PageSEO title="LodgeLink Admin Portal" />
      <Secure
        reqPermission={permission.view.dashboard}
        altChildren={<Error statusCode={403} statusMessage="Access Not Authorized" />}
      >
        <AdminDashboardContainer>
          {({ dashboardNums, loading, pendingRequestsCount }) => (
            <GridContainer>
              <GridRow justify="center">
                <Grid.Col md={12} lg={9}>
                  <InfoContainer
                    noPadding
                    leftSubBarContents={[{ type: 'label', props: { label: 'Today: Quick Details' } }]}
                  >
                    <StyledTable
                      viewHeader={false}
                      minTableWidth="100%"
                      contentCellsConfig={{
                        checkin: { style: cellStyle },
                        checkout: { style: cellStyle },
                        created: { style: cellStyle },
                        updated: { style: cellStyle },
                        organizationRequests: { style: cellStyle },
                        userRequests: { style: cellStyle },
                        creditApplications: { style: cellStyle }
                      }}
                      rows={[
                        { id: 'checkin', label: '' },
                        { id: 'checkout', label: '' },
                        { id: 'created', label: '' },
                        { id: 'updated', label: '' },
                        { id: 'organizationRequests', label: '' },
                        { id: 'userRequests', label: '' },
                        { id: 'creditApplications', label: '' }
                      ]}
                    >
                      {renderDetailRows(loading, dashboardNums, pendingRequestsCount)}
                    </StyledTable>
                  </InfoContainer>
                </Grid.Col>
              </GridRow>

              <GridRow>
                <GridCol>
                  <LocaleLink route="Bookings" passHref>
                    <CardLink>
                      <ActionLink>
                        <PortalSVGIcon
                          selectedIcon="bookings"
                          width={iconSize}
                          height={iconSize}
                          iconColor={colors.colorGrey03}
                        />
                        <p>Bookings</p>
                      </ActionLink>
                    </CardLink>
                  </LocaleLink>
                </GridCol>

                <GridCol>
                  <LocaleLink route="Customers" passHref>
                    <CardLink>
                      <ActionLink>
                        <PortalSVGIcon
                          selectedIcon="customers"
                          width={iconSize}
                          height={iconSize}
                          iconColor={colors.colorGrey03}
                        />
                        <p>Customers</p>
                      </ActionLink>
                    </CardLink>
                  </LocaleLink>
                </GridCol>

                <GridCol>
                  <LocaleLink route="Suppliers" passHref>
                    <CardLink>
                      <ActionLink>
                        <PortalSVGIcon
                          selectedIcon="suppliers"
                          width={iconSize}
                          height={iconSize}
                          iconColor={colors.colorGrey03}
                        />
                        <p>Suppliers</p>
                      </ActionLink>
                    </CardLink>
                  </LocaleLink>
                </GridCol>

                <GridCol>
                  <LocaleLink route="COGS" passHref>
                    <CardLink>
                      <ActionLink>
                        <PortalSVGIcon
                          selectedIcon="accounting"
                          width={iconSize}
                          height={iconSize}
                          iconColor={colors.colorGrey03}
                        />
                        <p>Accounting</p>
                      </ActionLink>
                    </CardLink>
                  </LocaleLink>
                </GridCol>
              </GridRow>
            </GridContainer>
          )}
        </AdminDashboardContainer>
      </Secure>
    </>
  );
}

export default Dashboard;
